import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

export interface DeleteFileRequest {
  documentIds: string[];
  projectId: string;
  totalSize?: number;
}

export interface DeleteMultipleFoldersRequest {
  folderIds: string[];
  projectId: string;
  totalSize?: number;
}

export interface GetDeletedFilesRequest {
  userId: string;
  page?: number;
  pageSize?: number;
}

export interface RestoreFileRequest {
  documentIds: string[];
}

export interface RestoreFoldersRequest {
  folderIds: string[];
}

export interface ForceDeleteFoldersRequest {
  folderIds: string[];
}

export interface ForceDeleteFilesRequest {
  fileIds: string[];
  totalSize?: number;
}

interface DeleteFileState {
  loading: boolean;
  error: string | null;
  deletedFiles: any[];
  loadingDeletedFiles: boolean;
  totalDeletedFiles: number;
  currentPage: number;
  pageSize: number;
  pageCount: number;
  firstRowIndex: number;
  lastRowIndex: number;
}

const initialState: DeleteFileState = {
  loading: false,
  error: null,
  deletedFiles: [],
  loadingDeletedFiles: false,
  totalDeletedFiles: 0,
  currentPage: 1,
  pageSize: 20,
  pageCount: 0,
  firstRowIndex: 0,
  lastRowIndex: 0,
};

const deleteFileSlice = createSlice({
  name: "deleteFile",
  initialState,
  reducers: {
    deleteFileRequest: (state, _action: PayloadAction<DeleteFileRequest>) => {
      state.loading = true;
      state.error = null;
    },
    deleteMultipleFoldersRequest: (
      state,
      _action: PayloadAction<DeleteMultipleFoldersRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    getDeletedFilesRequest: (
      state,
      action: PayloadAction<GetDeletedFilesRequest>
    ) => {
      state.loadingDeletedFiles = true;
      state.error = null;
      state.currentPage = action.payload.page || 1;
    },
    getDeletedFilesSuccess: (
      state,
      action: PayloadAction<{
        files: any[];
        total: number;
        page: number;
        pageSize: number;
        pageCount: number;
        firstRowIndex: number;
        lastRowIndex: number;
      }>
    ) => {
      state.loadingDeletedFiles = false;
      state.deletedFiles = action.payload.files;
      state.totalDeletedFiles = action.payload.total;
      state.currentPage = action.payload.page;
      state.pageSize = action.payload.pageSize;
      state.pageCount = action.payload.pageCount;
      state.firstRowIndex = action.payload.firstRowIndex;
      state.lastRowIndex = action.payload.lastRowIndex;
      state.error = null;
    },
    getDeletedFilesFailure: (state, action: PayloadAction<string>) => {
      state.loadingDeletedFiles = false;
      state.error = action.payload;
    },
    restoreFileRequest: (state, _action: PayloadAction<RestoreFileRequest>) => {
      state.loading = true;
      state.error = null;
    },
    restoreFoldersRequest: (
      state,
      _action: PayloadAction<RestoreFoldersRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    forceDeleteFilesRequest: (
      state,
      _action: PayloadAction<ForceDeleteFilesRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    forceDeleteFoldersRequest: (
      state,
      _action: PayloadAction<ForceDeleteFoldersRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    deleteFileSuccess: (state) => {
      state.loading = false;
      state.error = null;
    },
    deleteFileFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
  },
});

export const deleteFileActions = deleteFileSlice.actions;
export const deleteFileReducer = deleteFileSlice.reducer;
